import { makeStyles, Menu, MenuButton, MenuItem, MenuList, MenuPopover, MenuTrigger, shorthands, Tooltip, Button } from '@fluentui/react-components';
import { ArrowUpload16Regular, ArrowUpload24Regular, Cloud16Regular, CloudArrowUpRegular } from '@fluentui/react-icons';
import { CloudUpload } from 'lucide-react';
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useRef } from 'react';
import './AttachmentButton.scss';
import { useConversations, useFeature } from '../../store/hooks';
import { useConfirm } from '../../Contexts/hooks';
import { t } from 'i18next';
import { UserInteractionEventProperties, EventProperty_Value_ApplicationType, EventProperty_Value_UploadDocument, EventName_UserInteraction } from '../../store/models/customEvents';
import { useUserContext } from '../../Contexts/UserContext';
import { appInsights } from '../../AppInsights';
import { getAttachmentExtensionTypeByFilename } from '../../Utils/file';
import useGooglePicker from '../../Hooks/useGooglePicker';
import useGdriveDownload from '../../Hooks/useGdriveDownload';
import { AttachmentSourceType } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';

const useStyles = makeStyles({
    iconContainer: {
        display: 'flex',
        flexDirection: 'row',
        height: '35px',
        alignItems: 'center',
        ...shorthands.gap('10px'),
    },
});

export interface AttachmentButtonProps {
    enabled: boolean;
    onClick: (files: FileList, source: AttachmentSourceType) => void;
    setIsDriveFileDownloading: Dispatch<SetStateAction<boolean>>;
    onImprovePrompt?: () => void;
}

// manages the attachment of a file. It allows the user to upload a file from the local drive or from Google Drive
// it performs all the checks before sending it to the parent component
export const AttachmentButton: FC<AttachmentButtonProps> = (props: AttachmentButtonProps) => {
    const styles = useStyles();
    const hiddenFileInput = useRef(null as HTMLInputElement | null);
    const { userProfile } = useUserContext();
    const confirm = useConfirm();
    const { enabled, onClick, setIsDriveFileDownloading, onImprovePrompt } = props;
    const { activeChat, activeGpt } = useConversations();
    
    const { getFilesByIds } = useGdriveDownload(confirm, activeGpt?.attachment?.extensions || []);

    const onUploadDocumentAddRef = useRef<(files: FileList, source: AttachmentSourceType) => void>();

    const onUploadDocumentAdd = useCallback(
        (files: FileList, source: AttachmentSourceType) => {
            // Track user interaction on Application Insights
            var customEvent = {
                name: EventName_UserInteraction,
                properties: {
                    applicationType: EventProperty_Value_ApplicationType,
                    userId: userProfile?.username,
                    interactionType: EventProperty_Value_UploadDocument,
                } as UserInteractionEventProperties,
            };

            if (files.length === 0) {
                customEvent.properties.isSuccess = false;
                customEvent.properties.error = 'noFiles';
                appInsights.trackEvent(customEvent);
                return;
            }

            // Helper function to validate a single file
            const processFile = (file: File): boolean => {
                if (activeChat?.attachments?.some((a) => a.name?.toLocaleLowerCase() === file.name.toLocaleLowerCase())) {
                    confirm({
                        show: true,
                        title: t(`application.dialog.warningTitle`),
                        message: t(`conversation.attachments.fileAlreadyAttached`),
                        confirmText: t('Ok'),
                    });
                    return false;
                }

                if (activeGpt?.attachment?.extensions && !activeGpt?.attachment?.extensions?.some((ext) => file.name.toLowerCase().endsWith(ext.value!))) {
                    customEvent.properties.isSuccess = false;
                    customEvent.properties.error = 'fileNotSupported';
                    appInsights.trackEvent(customEvent);

                    confirm({
                        show: true,
                        title: t(`application.dialog.warningTitle`),
                        message: `${t(`conversation.attachments.fileNotSupported`)} ${activeGpt?.attachment?.extensions?.map((a) => a.value).join(', ')}.`,
                        confirmText: t('Ok'),
                    });
                    return false;
                }

                if (file.size === 0) {
                    customEvent.properties.isSuccess = false;
                    customEvent.properties.error = 'fileEmpty';
                    appInsights.trackEvent(customEvent);

                    confirm({
                        show: true,
                        title: t(`application.dialog.warningTitle`),
                        message: t(`conversation.attachments.fileEmpty`),
                        confirmText: t('Ok'),
                    });
                    return false;
                }

                if (activeGpt?.attachment?.maxSizeMB && file.size / 1000000 > activeGpt?.attachment?.maxSizeMB) {
                    customEvent.properties.isSuccess = false;
                    customEvent.properties.error = 'fileTooLarge';
                    appInsights.trackEvent(customEvent);

                    confirm({
                        show: true,
                        title: t(`application.dialog.warningTitle`),
                        message: `${t(`conversation.attachments.fileTooLarge`)} ${activeGpt?.attachment?.maxSizeMB} MB`,
                        confirmText: t('Ok'),
                    });
                    return false;
                }
                if (
                    activeGpt?.attachment?.maxTotalFilesSizeMB &&
                    (activeChat?.attachments?.reduce((total, attachment) => total + attachment.sizeMB!, 0) ?? 0) + file.size / 1000000 > activeGpt?.attachment?.maxTotalFilesSizeMB
                ) {
                    customEvent.properties.isSuccess = false;
                    customEvent.properties.error = 'totalFilesTooLarge';
                    appInsights.trackEvent(customEvent);

                    confirm({
                        show: true,
                        title: t(`application.dialog.warningTitle`),
                        message: `${t(`conversation.attachments.totalFilesTooLarge`)} ${activeGpt?.attachment?.maxTotalFilesSizeMB} MB`,
                        confirmText: t('Ok'),
                    });
                    return false;
                }

                return true;
            };

            const dataTransfer = new DataTransfer();

            Array.from(files).forEach((file) => {
                if (processFile(file)) {
                    dataTransfer.items.add(file);
                }
            });

            // Call the onClick handler with all valid files
            if (dataTransfer.files.length > 0) {
                onClick(dataTransfer.files, source);
            }
        },
        [onClick, confirm, userProfile, activeChat?.attachments, activeGpt?.attachment],
    );

    useEffect(() => {
        onUploadDocumentAddRef.current = onUploadDocumentAdd;
    }, [onUploadDocumentAdd]);

    // Handle Google Picker Callback
    const onPickerCallback = async (data: any): Promise<void> => {
        if (data.action === window.google.picker.Action.PICKED) {
            const fileIds = (data.docs as any[]).map((x) => x.id);
            const fileNames = (data.docs as any[]).map((x) => x.name);

            setIsDriveFileDownloading(true);
            const files: FileList | null = await getFilesByIds(fileIds, fileNames);
            setIsDriveFileDownloading(false);

            if (files) {
                onUploadDocumentAddRef.current!(files, AttachmentSourceType.GoogleDrive);
            }
        }
    };

    const { openPicker } = useGooglePicker(onPickerCallback, activeGpt?.attachment?.extensions?.map((x) => x.mimeType).join(',') || '');

    const onUploadFromLocalDriveClick = useCallback(() => {
        if (hiddenFileInput.current) {
            hiddenFileInput.current.click();
            hiddenFileInput.current.value = '';
        }
    }, []);

    const onUploadFromGoogleDriveClick = useCallback(() => {
        const expiresBy = localStorage.getItem('expiresBy');
        if (localStorage.getItem('pc_t') && expiresBy) {
            if (new Date().getTime() >= new Date(expiresBy).getTime()) {
                localStorage.removeItem('pc_t');
                localStorage.removeItem('expiresBy');
            }
        }
        openPicker();
    }, []);

    return (
        <div className="attachment-button-row">
            <Menu>
                <MenuTrigger>
                        <Tooltip content={enabled ? 'Upload a document' : 'You have reached the maximum number of files'} relationship='label'>
                        <MenuButton icon={<CloudUpload size={40} strokeWidth={1.5} />} appearance='transparent' size='medium' disabled={!enabled}></MenuButton>
                    </Tooltip>
                </MenuTrigger>
                <MenuPopover>
                    <MenuList>
                        <MenuItem onClick={onUploadFromLocalDriveClick}>
                            <div className={styles.iconContainer}>
                                <CloudUpload size={24} strokeWidth={2} width={18} height={14} />
                                Upload from your device
                            </div>
                        </MenuItem>
                        <MenuItem onClick={onUploadFromGoogleDriveClick}>
                            <div className={styles.iconContainer}>
                                <img src='/GoogleDrive-icon.svg' width='24px' />
                                Upload from Google Drive
                            </div>
                        </MenuItem>
                    </MenuList>
                </MenuPopover>
            </Menu>
            <input
                multiple
                type='file'
                accept={activeGpt?.attachment?.extensions?.map((x) => x.value)?.join(', ')}
                ref={hiddenFileInput}
                hidden
                onChange={(event) => onUploadDocumentAdd(event.target.files!, AttachmentSourceType.LocalDrive)}
            />
        </div>
    );
};
